import { Component } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'

import { getFromLocalStorage, setToLocalStorage, removeFromLocalStorage } from '../utils/functions'
import AuthService from '../redux/middlewares/auth'

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

const queueFailedRequest = (originalRequest) => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject })
  }).then((token) => {
    originalRequest.headers.Authorization = `Bearer ${token}`
    return axios(originalRequest)
  })
}

const refreshTokens = async () => {
  const refreshToken = getFromLocalStorage('refresh_token')
  if (!refreshToken) return null

  const response = await AuthService.refreshAccessToken(refreshToken)
  const { accessToken, refreshToken: newRefreshToken } = response.data.data

  setToLocalStorage('access_token', accessToken)
  setToLocalStorage('refresh_token', newRefreshToken)

  return accessToken
}

const clearAuthAndLogout = (logoutRequest) => {
  removeFromLocalStorage('access_token')
  removeFromLocalStorage('refresh_token')
  logoutRequest()
}

const retryRequestWithToken = (originalRequest, token) => {
  originalRequest.headers.Authorization = `Bearer ${token}`
  return axios(originalRequest)
}

const withAuth = (WrappedComponent) => {
  class ComposedComponent extends Component {
    requestInterceptor = axios.interceptors.request.use((req) => {
      req.headers.Authorization = `Bearer ${getFromLocalStorage('access_token')}`
      return req
    })

    responseInterceptor = axios.interceptors.response.use(
      (res) => res,
      async (err) => {
        const { logoutRequest } = this.props
        const originalRequest = err.config || {}
        const { status } = err.response || {}

        if (originalRequest._skipAuthInterceptor) {
          return Promise.reject(err)
        }

        const isAuthError = status === 401 || status === 403
        if (!isAuthError || originalRequest._retry) {
          return Promise.reject(err)
        }

        if (isRefreshing) {
          return queueFailedRequest(originalRequest)
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const newToken = await refreshTokens()
          if (!newToken) {
            logoutRequest()
            return Promise.reject(err)
          }

          processQueue(null, newToken)
          return retryRequestWithToken(originalRequest, newToken)
        } catch (refreshError) {
          processQueue(refreshError, null)
          clearAuthAndLogout(logoutRequest)
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      },
    )

    componentWillUnmount() {
      axios.interceptors.request.eject(this.requestInterceptor)
      axios.interceptors.response.eject(this.responseInterceptor)
    }

    render() {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return <WrappedComponent {...this.props} />
    }
  }

  ComposedComponent.propTypes = {
    logoutRequest: PropTypes.func,
  }

  ComposedComponent.defaultProps = {
    logoutRequest: () => {},
  }

  return ComposedComponent
}

export default withAuth
